import { useSelector } from 'react-redux';
import { createSelector } from 'reselect';
import { routesIntlMapping } from '../navigator/types';
import { RootState } from '../types';
import { AppState, CustomerType, Features, initialAppState } from './types';
import { Country } from '@buy-viasat/types/build/bv';

export const toPascalCase = (stringToConvert: string): string => {
  const routeAlias = routesIntlMapping[stringToConvert] || stringToConvert;
  return `${routeAlias.charAt(0).toUpperCase()}${routeAlias.slice(1)}`;
};

export const selectAppDomain = (state: RootState): AppState => {
  return state?.app || initialAppState;
};

export const selectLoadedFeatureFlags = createSelector(
  [selectAppDomain],
  (state: AppState) => state.loadedFeatureFlags,
);

export const selectFeatureFlags = createSelector([selectAppDomain], (state: AppState) => state.featureFlags);
export const useFeatureFlags = (): Features => useSelector(selectFeatureFlags);

export const selectLoadedCidPhoneMap = createSelector(
  [selectAppDomain],
  (state: AppState) => state.loadedCidPhoneMap,
);

// The CID-matched phone number, resolved server-side (see getCidPhoneMap resolver), falling back
// to the SSM-configured default, or undefined (letting callers apply their own static fallback)
// if it hasn't loaded yet.
export const selectCidPhoneNumber = createSelector(
  [selectAppDomain],
  (state: AppState) => state.cidPhoneNumber || undefined,
);

export const selectCidPhoneNumberCampaignId = createSelector(
  [selectAppDomain],
  (state: AppState) => state.cidPhoneNumberCampaignId,
);

export const selectIsVs2LargePlan = createSelector(
  [selectAppDomain],
  (state: AppState) => state.country === Country.US,
);

export const selectBillingContactEqualsAccountHolder = createSelector(
  [selectAppDomain],
  (state: AppState) => state.billingContactEqualsAccountHolder,
);

export const selectOnSiteContactEqualsAccountHolder = createSelector(
  [selectAppDomain],
  (state: AppState) => state.onSiteContactEqualsAccountHolder,
);

export const selectCreditCheckResults = createSelector(
  [selectAppDomain],
  (state: AppState) => state.creditCheckResults,
);

export const selectCurrentModal = createSelector([selectAppDomain], (state: AppState) => state.currentModal);
export const selectLocale = createSelector([selectAppDomain], (state: AppState) => state.locale);
export const selectCurrencySymbol = createSelector([selectAppDomain], (state: AppState) => state.currencySymbol);
export const selectCustomerType = createSelector([selectAppDomain], (state: AppState) => state.customerType);
export const selectIsBusinessUser = createSelector(
  [selectAppDomain],
  (state: AppState) => state.customerType === CustomerType.B,
);
export const selectCountry = createSelector([selectAppDomain], (state: AppState) => state.country);
export const selectIsAvailable = createSelector([selectAppDomain], (state: AppState) => state.isAvailable);
export const selectIsCartVisible = createSelector([selectAppDomain], (state: AppState) => state.isCartVisible);


export const selectIsModalVisible = createSelector([selectAppDomain], (state: AppState) => state.isModalVisible);
export const selectIsOrderSubmitting = createSelector([selectAppDomain], (state: AppState) => state.isOrderSubmitting);
export const selectIsPaymentInformationComplete = createSelector(
  [selectAppDomain],
  (state: AppState) => state.isPaymentInformationComplete,
);

export const selectIsPersonalInformationComplete = createSelector(
  [selectAppDomain],
  (state: AppState) => state.isPersonalInformationComplete,
);

export const selectIsScheduleInstallationComplete = createSelector(
  [selectAppDomain],
  (state: AppState) => state.isScheduleInstallationComplete,
);

export const selectIsTaxCodesLoading = createSelector([selectAppDomain], (state) => state.isTaxCodesLoading);
export const selectLocation = createSelector([selectAppDomain], (state) => state.location);
export const selectPartyId = createSelector([selectAppDomain], (state: AppState) => state.partyId);
export const selectOrderId = createSelector([selectAppDomain], (state: AppState) => state.orderId);
export const selectRelnId = createSelector([selectAppDomain], (state: AppState) => state.relnId);
export const selectRedirected = createSelector([selectAppDomain], (state: AppState) => state.redirected);
export const selectRouteName = createSelector([selectAppDomain], (state: AppState) => toPascalCase(state.currentRoute));
export const selectServiceabilityError = createSelector(
  [selectAppDomain],
  (state: AppState) => state.serviceabilityError,
);
export const selectStartTime = createSelector([selectAppDomain], (state: AppState) => new Date(state.startTime));
export const selectUrlParams = createSelector([selectAppDomain], (state: AppState) => state.urlParams);
export const selectWorkOrderId = createSelector([selectAppDomain], (state: AppState) => state.workOrderId);

export const selectSalesAgreementId = createSelector([selectAppDomain], (state: AppState) => state.salesAgreementId);

export const selectFulfillmentAgreementId = createSelector(
  [selectAppDomain],
  (state: AppState) => state.fulfillmentAgreementId,
);

export const selectErrorOverrideBody = createSelector(
  [selectAppDomain],
  (state: AppState) => state.errorModalOverrideBody,
);

export const selectErrorOverrideHeader = createSelector(
  [selectAppDomain],
  (state: AppState) => state.errorModalOverrideHeader,
);

export const selectErrorRetryAction = createSelector([selectAppDomain], (state: AppState) => state.errorRetryAction);

export const selectErrorOverrideButtonText = createSelector(
  [selectAppDomain],
  (state: AppState) => state.errorModalOverrideButtonText,
);

export const selectErrorOverrideSvg = createSelector(
  [selectAppDomain],
  (state: AppState) => state.errorModalOverrideSvg,
);

export const selectErrorModalAttempts = createSelector(
  [selectAppDomain],
  (state: AppState) => state.errorRetryAttempts,
);

export const selectDisplayCalculatedLaterCartLabel = createSelector(
  [selectAppDomain],
  (state: AppState) => state.displayCalculatedLaterCartLabel,
);

export const selectSmbLinkServiceabilityRedirect = createSelector(
  [selectAppDomain],
  (state: AppState) => state.smbLinkResidentialRedirect,
);

export const selectSmbLinkPlansRedirect = createSelector(
  [selectAppDomain],
  (state: AppState) => state.smbLinkPlansRedirect,
);

export const selectIsStandAlone = createSelector([selectAppDomain], (state: AppState) => state.isStandAlone);

export const selectIsEmbedded = createSelector([selectAppDomain], (state: AppState) => state.isEmbedded);

export const selectHideMfeCTA = createSelector([selectAppDomain], (state: AppState) => state.hideMfeCTA);
